import React from "react";
import ReactECharts from "echarts-for-react";
import PropTypes from "prop-types";

const WaterfallChart = React.memo((props) => {
  const {
    chartData,
    legendDataArray,
    toolTipConfig,
    xAxisData,
    yField,
    xField,
    xnamegap,
    yAxisGap,
    labelConfig,
    isBarMultiple,
    chartheight,
    increase,
    decrease,
    Line,
    ynamegap,
    formaterYaxis,
    showLabelInside,
    events,
    Format,
    FormatMargin,
    selectedMode,
  } = props;

  const modifiedLine = Line.slice(0, -1);
  const I = increase;
  const E = decrease;
  let a = [0];
  let fi = [];
  let li = [];

  function nearZero(value, epsilon = 1e-10) {
    return Math.abs(value) < epsilon ? 0 : value;
  }

  I.forEach((item, index) => {
    let income = item !== "-" ? parseFloat(item) : 0;
    let expense = E[index] !== "-" ? parseFloat(E[index]) : 0;
    let cumulative = a[index] + income - expense;
    cumulative = nearZero(cumulative);
    a.push(cumulative);

    if (item === "-") {
      if (I[index - 1] !== "-" && I[index - 1] !== undefined) {
        fi.push(index);
      }
      if (I[index + 1] !== "-" && I[index + 1] !== undefined) {
        li.push(index);
      }
    }
  });

  for (let i = 0; i < I.length; i++) {
    if (I[i] === "-" && E[i] === "-") {
      a[i + 1] = a[i];
    }
  }

  fi.forEach((item, index) => {
    const adjustedIndex = item - fi.slice(0, index).filter((i) => i < item).length;
    a.splice(adjustedIndex, 1);
  });

  li.forEach((item) => {
    a.splice(item, 0, a[item]);
  });

  const s = a;
  const maxTotalValue = Math.max(...s);
  const tenPercentMaxTotal = maxTotalValue * 0.1;

  const updatedDecreaseData = decrease.map((item, index) => {
    if (item !== "-" && s[index] <= tenPercentMaxTotal) {
      return {
        ...item,
        value: item,
        label: {
          position: "top",
        },
      };
    }
    return item;
  });

  const modifiedChartData = chartData.map((item) => {
    if (item.name === "Decrease") {
      return {
        ...item,
        data: updatedDecreaseData,
      };
    }
    return item;
  });

 const formatYAxis = (value) => { //y axis data calcu
  const abs = Math.abs(value); //abs refers to absolute value

  if (abs >= 1_000_000_000_000) {return `${(value / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '')}T`;
  }
  if (abs >= 1_000_000_000) {return `${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (abs >= 1_000_000) {return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return Math.round(value).toString();
};

  const maxDigits = Math.max(
    ...s.map((value) => formatYAxis(value).toString().length)
  );

  const nameGapYAxis = (maxDigits) => {
    let nameGap = 29; // Default value for 2-digit numbers
  if (maxDigits === 3) {nameGap = 40;}
  else if (maxDigits === 4) {nameGap = 45;}
  else if (maxDigits === 5) {nameGap = 48;}
  else if (maxDigits === 6) {nameGap = 50;}
  else if (maxDigits === 7) {nameGap = 52;}
  return nameGap ;
  };

  const YaxisTruncate = (value) => {
    return value.length > 4 ? value.substring(0, 4) + "..." : value;
  };

  const getBarOption = () => ({
    tooltip: toolTipConfig || {
      trigger: "item",
    },
    legend: {
      data: legendDataArray,
      left: "left",
      icon: "circle",
      selectedMode: selectedMode ? selectedMode : false,
    },
    grid: {
      left: "5%",
      right: "2%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      name: xField,
      nameGap: xnamegap || 38,
      nameLocation: "middle",
      data: xAxisData,
    },
    yAxis: {
      type: "value",
      name: yField,
      show: true,
      nameLocation: "middle",
      nameGap: formaterYaxis ? ynamegap : nameGapYAxis(maxDigits),
      axisLabel: {
        margin: FormatMargin || 10,
        show: !showLabelInside,
        formatter: Format
          ? formatYAxis
          : formaterYaxis
          ? YaxisTruncate
          : formatYAxis,
      },
    },
    series: [
      ...modifiedChartData.map((seriesData) => ({
        name: seriesData.name,
        silent: seriesData.silent,
        type: "bar",
        barWidth: 80,
        z: 10,
        barCategoryGap: "40%",
        stack: "total",
        label: {
          show: seriesData.label,
          position: seriesData.labelposition || "top",
          ...labelConfig,
        },
        itemStyle: {
          color: seriesData.color,
        },
        emphasis: {
          itemStyle: {
            borderColor: seriesData.color,
            color: seriesData.color,
          },
        },
        data: seriesData.name === "Total" ? s : seriesData.data,
      })),
      {
        name: "Electricity",
        type: "line",
        z: 0,
        itemStyle: { color: "#979797" },
        step: "start",
        smooth: true,
        symbol: "none",
        data: modifiedLine,
      },
    ],
  });

  let chartInstance = null;

  const handleChartReady = (chart) => {
    chartInstance = chart;
  };

  const handleChartClick = () => {};

  const handleLegendSelectChanged = (params) => {
    const selectedValues = Object.values(params.selected);
    const allFalse = selectedValues.every((value) => value === false);

    const yAxisConfig = {
      yAxis: {
        type: "value",
        name: yField,
        show: !allFalse,
        nameLocation: "middle",
        nameGap: formaterYaxis ? ynamegap : nameGapYAxis(maxDigits),
        axisLabel: {
          show: !showLabelInside,
          formatter: formaterYaxis ? YaxisTruncate : formatYAxis,
        },
      },
    };

    if (chartInstance) {
      chartInstance.setOption(yAxisConfig);
    }
  };

  const onEvents = {
    click: handleChartClick,
    legendselectchanged: handleLegendSelectChanged,
  };

  return (
    <div style={{ height: chartheight === true ? "24vw" : null }}>
      <ReactECharts
        option={getBarOption()}
        onEvents={events ? onEvents : {}}
        onChartReady={handleChartReady}
        style={{ height: "400px", width: "100%" }}
      />
    </div>
  );
});

WaterfallChart.displayName = "WaterfallChart";

WaterfallChart.propTypes = {
  Line: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      silent: PropTypes.bool,
      label: PropTypes.bool,
      labelposition: PropTypes.string,
      color: PropTypes.string,
      data: PropTypes.array,
    })
  ).isRequired,
  chartheight: PropTypes.bool,
  decrease: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  increase: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  labelConfig: PropTypes.object,
  legendDataArray: PropTypes.arrayOf(PropTypes.string).isRequired,
  toolTipConfig: PropTypes.object,
  xAxisData: PropTypes.arrayOf(PropTypes.string).isRequired,
  xField: PropTypes.string,
  xnamegap: PropTypes.number,
  yField: PropTypes.string,
  ynamegap: PropTypes.number,
  formaterYaxis: PropTypes.bool,
  showLabelInside: PropTypes.bool,
  events: PropTypes.bool,
  Format: PropTypes.bool,
  selectedMode: PropTypes.bool,
  FormatMargin: PropTypes.number,
};

export default WaterfallChart;



